import { useState, type ReactNode } from "react";
import type { Certificate, Container, LabHealth, Part, Series } from "./types";
import { duration, gib, pct, since } from "./types";

// The Lab health page's panels. Each renders its own part, or says why that part is missing.

export function Unavailable({ reason }: { reason: string }) {
  return <div className="empty" style={{ margin: "0 20px 20px", minHeight: 56 }}>{reason}</div>;
}

/** The last hour as 5-minute bars on a 0–100% scale; faint gridlines at 50 and 100. */
export function Bars({ series, label }: { series: Series; label: string }) {
  const W = 300;
  const H = 56;
  const n = Math.max(series.points.length, 12);
  const slot = W / n;
  const bw = Math.max(2, slot - 2);
  return (
    <figure style={{ margin: 0 }}>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} preserveAspectRatio="none" role="img" aria-label={`${label}, last hour: average ${pct(series.avg)}, peak ${pct(series.max)}`}>
        {[0, 0.5].map((f) => <line key={f} x1="0" x2={W} y1={f * (H - 1) + 0.5} y2={f * (H - 1) + 0.5} stroke="var(--rule)" vectorEffect="non-scaling-stroke" />)}
        <line x1="0" x2={W} y1={H - 0.5} y2={H - 0.5} stroke="var(--border)" vectorEffect="non-scaling-stroke" />
        {series.points.map((p, i) => {
          const h = Math.max(1, (Math.min(100, p.v) / 100) * (H - 1));
          const x = (n - series.points.length + i) * slot + 1;
          return (
            <g key={p.t} className="bar-hit">
              <rect x={x - 1} y={0} width={slot} height={H} fill="transparent" />
              <rect x={x} y={H - 1 - h} width={bw} height={h} rx="1" fill="var(--accent)" />
              <title>{`${new Date(p.t).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })} · ${p.v.toFixed(1)}%`}</title>
            </g>
          );
        })}
      </svg>
      <figcaption className="small muted" style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
        <span>{label} · last hour</span>
        <span className="num">avg {pct(series.avg)} · peak {pct(series.max)}</span>
      </figcaption>
    </figure>
  );
}

/* ---------------- findings ---------------- */

const SEVERITY = {
  critical: { label: "Critical", pill: "negative" },
  warning: { label: "Warning", pill: "warning" },
  info: { label: "Note", pill: "" },
} as const;

export function Findings({ h }: { h: LabHealth }) {
  const [all, setAll] = useState(false);
  const shown = all ? h.findings : h.findings.slice(0, 8);
  return (
    <section className="panel flush" aria-labelledby="findings-title">
      <div className="panel-head"><h2 id="findings-title">Findings</h2><span className="stamp num">{h.findings.length}</span></div>
      <div className="list">
        {shown.map((f, i) => (
          <div className="item" key={`${f.title}-${i}`} style={{ gridTemplateColumns: "88px minmax(0, 1fr)" }}>
            <span className={`pill ${SEVERITY[f.severity].pill}`} style={{ justifySelf: "start" }}>{SEVERITY[f.severity].label}</span>
            <div>
              <div className="cell-main">{f.title}</div>
              <div className="cell-sub">{f.detail}</div>
            </div>
          </div>
        ))}
        {h.findings.length > 8 && (
          <div className="item" style={{ display: "flex", justifyContent: "center" }}>
            <button className="btn ghost" onClick={() => setAll(!all)}>{all ? "Show the first 8" : `Show all ${h.findings.length} findings`}</button>
          </div>
        )}
      </div>
    </section>
  );
}

/* ---------------- containers ---------------- */

function ContainerState({ c }: { c: Container }) {
  const tone = c.problem ? "down" : c.state === "running" ? (c.health === "starting" ? "unknown" : "up") : c.state === "exited" ? "stopped" : "unknown";
  const word = c.problem ?? (c.state === "running" ? (c.health ? `running · ${c.health}` : "running") : c.state === "exited" ? "exited 0" : c.state);
  return <span className={`health ${tone}`}><span className="dot" />{word}</span>;
}

export function ContainersPanel({ part }: { part: LabHealth["containers"] }) {
  return (
    <section className="panel flush" aria-labelledby="containers-title">
      <div className="panel-head">
        <h2 id="containers-title">Containers</h2>
        {part.available && <span className="stamp num">{part.healthy} of {part.expected} healthy · {part.projects.length} projects</span>}
      </div>
      {!part.available ? <Unavailable reason={part.reason} /> : (
        <div className="table-wrap">
          <table>
            <thead><tr><th>Service</th><th>State</th><th className="num">Restarts</th><th className="hide-phone">Up since</th></tr></thead>
            {part.projects.map((p) => (
              <tbody key={p.project}>
                <tr><td colSpan={4} className="cell-main mono" style={{ background: "var(--raised)", paddingTop: 8, paddingBottom: 8 }}>{p.project}</td></tr>
                {p.containers.map((c) => (
                  <tr key={c.name}>
                    <td><div>{c.service || c.name}</div><div className="cell-sub mono truncate" style={{ maxWidth: 280 }}>{c.image}</div></td>
                    <td><ContainerState c={c} /></td>
                    <td className="num">{c.restarts}</td>
                    <td className="hide-phone muted">{since(c.startedAt)}</td>
                  </tr>
                ))}
              </tbody>
            ))}
          </table>
        </div>
      )}
    </section>
  );
}

/* ---------------- certificates ---------------- */

function DaysLeft({ c }: { c: Certificate }) {
  if (c.daysLeft === null) return <span className="pill warning">not checked</span>;
  if (c.daysLeft < 0) return <span className="pill negative">expired</span>;
  if (!c.trusted) return <span className="pill negative">not trusted</span>;
  if (c.daysLeft < 21) return <span className="pill warning num">{c.daysLeft} days</span>;
  return <span className="num">{c.daysLeft} days</span>;
}

export function CertificatesPanel({ part }: { part: LabHealth["certificates"] }) {
  return (
    <section className="panel flush" aria-labelledby="certs-title">
      <div className="panel-head">
        <h2 id="certs-title">Certificates</h2>
        {part.available && <span className="stamp">asked at {part.target}</span>}
      </div>
      {!part.available ? <Unavailable reason={part.reason} /> : (
        <div className="table-wrap">
          <table>
            <thead><tr><th>Host</th><th className="hide-phone">Issuer</th><th>Expires</th><th className="num">Days left</th></tr></thead>
            <tbody>
              {part.sites.map((c) => (
                <tr key={c.host}>
                  <td><a className="cell-main" href={`https://${c.host}`} target="_blank" rel="noreferrer">{c.host}</a><div className="cell-sub"><span className="mono">{c.file}</span>{c.problem && <span className={c.trusted ? "" : "error"}> · {c.problem}</span>}</div></td>
                  <td className="hide-phone muted">{c.issuer ?? "—"}</td>
                  <td className="muted" style={{ whiteSpace: "nowrap" }}>{c.notAfter ? new Date(c.notAfter).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—"}</td>
                  <td className="num"><DaysLeft c={c} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/* ---------------- database and host ---------------- */

function Dl({ rows }: { rows: [string, ReactNode][] }) {
  return <dl className="kv">{rows.map(([k, v]) => <div key={k} style={{ display: "contents" }}><dt>{k}</dt><dd>{v}</dd></div>)}</dl>;
}

export function DatabasePanel({ part, backups }: { part: LabHealth["database"]; backups: LabHealth["backups"] }) {
  return (
    <section className="panel flush" aria-labelledby="db-title">
      <div className="panel-head"><h2 id="db-title">Database</h2>{part.available && <span className="stamp mono">{part.id}</span>}</div>
      {!part.available ? <Unavailable reason={part.reason} /> : (
        <div style={{ padding: "12px 20px 20px", display: "grid", gap: 16 }}>
          {part.metrics.available && <Bars series={part.metrics.cpu} label="CPU" />}
          <Dl rows={[
            ["Status", <span className={`pill ${part.status === "available" ? "positive" : "negative"}`}>{part.status}</span>],
            ["Class", <span className="mono">{part.class ?? "—"}</span>],
            ["Engine", `${part.engine ?? ""} ${part.engineVersion ?? ""}`.trim() || "—"],
            ["Storage", part.metrics.available && part.metrics.freeStorageBytes.latest !== null && part.allocatedGiB
              ? `${gib(part.metrics.freeStorageBytes.latest)} free of ${part.allocatedGiB} GiB`
              : `${part.allocatedGiB ?? "—"} GiB`],
            ["Connections", part.metrics.available ? <span className="num">{Math.round(part.metrics.connections.latest ?? 0)} now · peak {Math.round(Math.max(0, ...part.metrics.connections.points.map((p) => p.v)))}</span> : "—"],
            ["Memory free", part.metrics.available && part.metrics.freeableMemoryBytes.latest !== null ? gib(part.metrics.freeableMemoryBytes.latest) : "—"],
            ["Multi-AZ", part.multiAz ? "yes" : "no, one zone"],
            ["Backups", backups.database.available
              ? `${backups.database.retentionDays ?? 0} days kept · restorable to ${since(backups.database.latestRestorableTime)}`
              : backups.database.reason],
          ]} />
          {!part.metrics.available && <p className="small error">Metrics: {part.metrics.reason}</p>}
        </div>
      )}
    </section>
  );
}

function Value<T>({ part, render }: { part: Part<T>; render: (v: T) => ReactNode }) {
  return part.available ? <>{render(part)}</> : <span className="muted small">{part.reason}</span>;
}

export function HostPanel({ host, backups }: { host: LabHealth["host"]; backups: LabHealth["backups"] }) {
  return (
    <section className="panel flush" aria-labelledby="host-title">
      <div className="panel-head"><h2 id="host-title">Host</h2>{host.instance.available && <span className="stamp mono">{host.instance.id}</span>}</div>
      <div style={{ padding: "12px 20px 20px", display: "grid", gap: 16 }}>
        {host.cpu.available ? <Bars series={host.cpu} label="CPU" /> : <p className="small error">CPU: {host.cpu.reason}</p>}
        <Dl rows={[
          ["Instance", <Value part={host.instance} render={(i) => <><span className="mono">{i.type}</span> · {i.state}</>} />],
          ["Uptime", host.load.available && host.load.uptimeSeconds !== null
            ? duration(host.load.uptimeSeconds)
            : host.instance.available && host.instance.launchedAt ? `launched ${since(host.instance.launchedAt)}` : "—"],
          ["Load", <Value part={host.load} render={(l) => <span className="num">{l.one.toFixed(2)} · {l.five.toFixed(2)} · {l.fifteen.toFixed(2)}{l.cpus ? <span className="muted"> on {l.cpus} CPUs</span> : null}</span>} />],
          ["Memory", <Value part={host.memory} render={(m) => <span className="num">{gib(m.totalBytes - m.availableBytes)} used of {gib(m.totalBytes)}</span>} />],
          ["Disk", <Value part={host.disk} render={(d) => <span className="num">{gib(d.freeBytes)} free of {gib(d.sizeBytes)}</span>} />],
          ["Docker", <Value part={host.dockerDisk} render={(d) => <span className="num">{d.rows.map((r) => `${r.type} ${r.size}`).join(" · ") || "—"}</span>} />],
          ["Snapshots", <span className="muted">{backups.hostDisk.note}</span>],
        ]} />
      </div>
    </section>
  );
}
